/**
 * DraggableFillCell.jsx
 * Excel-like drag-to-fill component for controlled bulk editing
 * 
 * Features:
 * - Shows a small fill handle (square) at bottom-right of cell
 * - User can drag handle down to fill only selected rows
 * - Visual feedback during drag (highlighted cells)
 * - Prevents accidental bulk edits
 */
import React, { useState, useRef, useEffect } from 'react';
import EditableCell from './EditableCell';

const DraggableFillCell = ({
  value,
  type,
  options = [],
  onChange,
  onFillDown,
  onDragRangeChange, // New prop for real-time drag feedback
  disabled = false,
  recordIndex,
  totalRecords,
  field,
  className = ''
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragEndIndex, setDragEndIndex] = useState(null);
  const cellRef = useRef(null);
  const startIndexRef = useRef(null);

  const handleMouseUp = () => {
    if (isDragging && dragEndIndex !== null && dragEndIndex > startIndexRef.current) {
      // Apply fill from start to end
      const count = dragEndIndex - startIndexRef.current;
      onFillDown(startIndexRef.current, dragEndIndex, field, value);
    }
    setIsDragging(false);
    setDragEndIndex(null);
    startIndexRef.current = null;
    // Clear drag range in parent
    if (onDragRangeChange) {
      onDragRangeChange(null, null);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    // Find the row we're currently over
    const rows = document.querySelectorAll(`[data-fill-row]`);
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rect = row.getBoundingClientRect();
      if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
        const rowIndex = parseInt(row.getAttribute('data-fill-row'));
        if (rowIndex >= startIndexRef.current) {
          setDragEndIndex(rowIndex);
          // Notify parent of drag range change for row highlighting
          if (onDragRangeChange) {
            onDragRangeChange(startIndexRef.current, rowIndex);
          }
        }
        break;
      }
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('mousemove', handleMouseMove);
      return () => {
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [isDragging, dragEndIndex, field, value, onFillDown, onDragRangeChange]);

  const handleMouseDown = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    startIndexRef.current = recordIndex;
    setDragEndIndex(recordIndex);
    // Notify parent that drag started
    if (onDragRangeChange) {
      onDragRangeChange(recordIndex, recordIndex);
    }
  };

  // Check if this cell should be highlighted during drag
  const isInDragRange = isDragging && 
    recordIndex >= startIndexRef.current && 
    recordIndex <= dragEndIndex;

  const dragCount = dragEndIndex !== null && dragEndIndex > startIndexRef.current 
    ? dragEndIndex - startIndexRef.current 
    : 0;

  return (
    <div 
      className={`relative group ${isInDragRange ? 'bg-blue-200 border-2 border-blue-600 shadow-lg' : ''} ${className}`}
      ref={cellRef}
      style={isInDragRange ? { zIndex: 20 } : {}}
    >
      <div className="flex-grow min-w-0">
        <EditableCell
          value={value}
          type={type}
          options={options}
          onChange={onChange}
          disabled={disabled}
        />
      </div>
      
      {/* Show drag count indicator on the starting cell */}
      {isDragging && recordIndex === startIndexRef.current && dragCount > 0 && (
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-3 py-1 rounded-md text-sm font-semibold shadow-lg z-50 whitespace-nowrap">
          Filling {dragCount} row{dragCount !== 1 ? 's' : ''} below
        </div>
      )}
      
      {/* Fill handle - small square at bottom-right */}
      {!disabled && (
        <div
          className={`
            absolute bottom-0 right-0 w-2 h-2 
            bg-primary-600 hover:bg-primary-700
            border border-white
            cursor-ns-resize
            opacity-0 group-hover:opacity-100
            transition-all
            ${isDragging ? 'opacity-100 scale-150 bg-blue-600' : ''}
          `}
          onMouseDown={handleMouseDown}
          title="Drag down to fill cells"
          style={{ 
            borderRadius: '1px',
            zIndex: 10,
            boxShadow: isDragging ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.2)'
          }}
        />
      )}
    </div>
  );
};

export default DraggableFillCell;
